import { useCallback } from 'react';

interface ConstraintBuilderOptions<T> {
  /** This should contain ALL possible additional constraints for the query, even if they may not be in the final result */
  constraints: Partial<T>;

  /** This is enforced to be a subset of the keys of the generic type. The boolean values control whether the corresponding constraint ends up in the final result. */
  conditions: Partial<Record<keyof T, boolean>>;
}

interface ConstraintBuilder<T> {
  /**
   * Takes the base object and applies the results of processing the options to it.
   * @returns the completed object with all active constraints present and no inactive constraints
   */
  build(base: T, options: ConstraintBuilderOptions<T>): T;
}

/**
 * A hook that allows you to avoid repetitive assignments to the same object based on various boolean values
 * This is completely optional and may not be useful for all cases, but can be combined with other techniques.
 * An example is provided at the bottom of the file this hook is in.
 * @returns a results object containing a method for building up the object
 */
export default function useConstraintBuilder<T>(): ConstraintBuilder<T> {
  const build = useCallback((base: T, options: ConstraintBuilderOptions<T>): T => {
    const { constraints, conditions } = options;
    const result = { ...base };

    const keys = Object.keys(constraints);

    keys.forEach(key => {
      if (conditions[key]) {
        result[key] = { ...result[key], ...constraints[key] };
      } else {
        delete result[key];
      }
    });

    return result;
  }, []);

  return {
    build
  };
}

/*
 * This hook may be useful if you have code in the following pattern, within a hook or component:
 *
 * interface ExampleConstraints {
 *   example: string;
 *   somethingElse: string;
 *   somethingElse2: string;
 * }
 *
 * function exampleFunction(): ExampleConstraints {
 *   let constraints: ExampleConstraints = {
 *      example: 'example'
 *   };
 *
 *   if (something) {
 *     constraints = {
 *       ...constraints,
 *       somethingElse: 'example'
 *     }
 *   }
 *
 *   if (something2) {
 *     constraints = {
 *       ...constraints,
 *       somethingElse2: 'example'
 *     }
 *   }
 *
 *   return constraints;
 * }
 *
 * You may refactor the function using the hook:
 *
 * const constraintBuilder = useConstraintBuilder<ExampleConstraints>();
 *
 * function exampleFunction(): ExampleConstraints {
 *   return constraintBuilder.builder({ example: 'example' }, {
 *     constraints: {
 *       somethingElse: 'example',
 *       somethingElse2: 'example'
 *     },
 *     conditions: {
 *       somethingElse: something,
 *       somethignElse2: something2
 *     }
 *   });
 * }
 *
 */
